/**
 * vuelidate rules plus the backend's field messages for one form, both shown
 * under the field they belong to (AGENTS.md: inline field errors).
 */
import { computed } from 'vue';
import useVuelidate, { type ValidationArgs } from '@vuelidate/core';
import { useFormErrors } from '@/composables/useFormErrors';
import { cleanValidationMessage } from '@/utils/billCopy';

type FieldState = { $error?: boolean; $errors?: Array<{ $message: unknown }>; $touch?: () => void };

/**
 * vuelidate + server field errors for one form. `fieldError(field)` returns
 * the client message first, then whatever the server said for that field.
 */
export function useValidatedForm<T extends object>(rules: ValidationArgs | (() => ValidationArgs), state: T) {
  // $scope: false keeps each form independent: without it a form on a parent
  // page would also validate (and be blocked by) a dialog in a child component.
  const rulesRef = computed<ValidationArgs>(() => (typeof rules === 'function' ? rules() : rules));
  const v$ = useVuelidate(rulesRef, state, { $scope: false });
  const { serverErrors, handleServerErrors, clearErrors, getErrorMessage } = useFormErrors();

  function field(name: string): FieldState | undefined {
    return (v$.value as unknown as Record<string, FieldState>)[name];
  }

  function fieldError(name: string): string {
    const client = field(name);
    if (client?.$error && client.$errors?.[0]) return String(client.$errors[0].$message);
    const server = getErrorMessage(name);
    return server ? cleanValidationMessage(server) : '';
  }

  function touch(name: string): void {
    field(name)?.$touch?.();
  }

  /** Validates every field; shows messages on all of them. */
  async function validate(): Promise<boolean> {
    clearErrors();
    return v$.value.$validate();
  }

  function reset(): void {
    v$.value.$reset();
    clearErrors();
  }

  return { v$, fieldError, touch, validate, reset, handleServerErrors, serverErrors, clearErrors };
}
